import {
    ColoursObj,
    Icon,
    MainButton,
    MobileConfirmDialog,
    SelectInputField,
    TDialogStore,
    Typography,
    drawerStore,
    useDialogStore,
} from '@eventr/common-storybook'
import { Fab } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useGuestStore } from '../../../../feat/guest/guest.store'
import i18n from '../../../../i18n/i18next'
import { bs, useBoundStore } from '../../../../state/bound.store'
import {
    ALLOWED_TIERS,
    lls,
    shareIntervals,
    useLiveLocationStore,
} from '../../../../state/liveLocationState/liveLocation.store'
import { windowSizes } from '../../../../state/windowState/windowSignals'

interface liveLocationShareToggle {
    bottomFab?: boolean
}

export default function LiveLocationShareControl({
    bottomFab = false,
}: liveLocationShareToggle) {
    const sharingStaleTimestamp = useLiveLocationStore(
        useShallow((s) => s.sharingStaleTimestamp)
    )
    const sharingIsActive = sharingStaleTimestamp !== undefined

    const isGuestMode = useGuestStore(useShallow((s) => s.isGuestMode))

    const { disableLocationServices, tier } = useBoundStore(
        useShallow((s) => {
            return {
                disableLocationServices: s.orgConfig.disableLocationServices,
                tier: s.tier,
            }
        })
    )

    const getItinTitle = (): string => {
        const eventInfo = bs().getEventInfoV2()
        if (!eventInfo) {
            return i18n.t('Itinerary')
        }
        return eventInfo.data.title
    }
    const trancateItinTitle = (name: string): string => {
        if (name.length >= 20) {
            return name.slice(0, name.length / 2) + '...'
        }
        return name
    }

    const [interval, setInterval] = useState<number>(15 * 60 * 1000)
    const intervalRef = useRef(interval)

    useEffect(() => {
        intervalRef.current = interval
    }, [interval])

    const { replaceDialog, clearDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            clearDialog: s.clearDialog,
            replaceDialog: s.replaceDialog,
        }))
    )

    const onClick = () => {
        if (sharingIsActive) {
            replaceDialog({
                node: (
                    <MobileConfirmDialog
                        text={i18n.t('info-dialog.shareLiveLocationBody', {
                            itineraryName: getItinTitle(),
                        })}
                        // header={'Test'}
                        onConfirm={() => {
                            lls().stopSharingLiveLocation()
                            clearDialog()
                        }}
                        onCancel={() => {
                            clearDialog()
                        }}
                        cancelText={i18n.t('info-dialog.no')}
                        confirmText={i18n.t('info-dialog.yes')}
                    />
                ),
                backdropClosable: false,
            })
        } else {
            drawerStore().openDrawer({
                node: (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100vw',
                            backgroundColor: ColoursObj.backgrounds.primary,
                            borderRadius: '16px 16px 0px 0px',
                        }}
                    >
                        <div
                            style={{
                                padding: '16px',
                                paddingBottom: `${
                                    (windowSizes.value.insets?.insets.bottom ??
                                        0) + 16
                                }px`,
                                gap: '16px',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <div
                                style={{
                                    position: 'relative',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '32px',
                                }}
                            >
                                <Typography variant="bodyBold" align="center">
                                    {i18n.t('drawer.locationSharing.header')}
                                </Typography>

                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '8px',
                                        position: 'absolute',
                                        right: '0px',
                                        top: '0px',
                                    }}
                                >
                                    <div
                                        onClick={() => {
                                            drawerStore().closeDrawer()
                                        }}
                                        style={{
                                            borderRadius: '20px',
                                            padding: '4px',
                                            height: '24px',
                                            width: '24px',
                                            backgroundColor:
                                                ColoursObj.backgrounds
                                                    .secondary,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Icon name="Exit" />
                                    </div>
                                </div>
                            </div>

                            <SelectInputField
                                data={shareIntervals}
                                fieldLabel={i18n.t(
                                    'drawer.locationSharing.duration'
                                )}
                                defaultValue={interval}
                                onChange={(value) => {
                                    setInterval(value.target.value as number)
                                }}
                            />

                            <MainButton
                                id="shareWithItineraryButton"
                                style={{ width: '100%' }}
                                icon="Share_Location"
                                iconPosition="left"
                                onClick={() => {
                                    const latestInterval = intervalRef.current
                                    const matchedInterval = shareIntervals.find(
                                        (item) => item.id === latestInterval
                                    )

                                    lls().shareLiveLocation(latestInterval)
                                    drawerStore().closeDrawer()
                                }}
                                text={i18n.t(
                                    'drawer.locationSharing.shareWith',
                                    {
                                        itineraryName: trancateItinTitle(
                                            getItinTitle()
                                        ),
                                    }
                                )}
                            />
                        </div>
                    </div>
                ),
                onBackdropClick: () => drawerStore().closeDrawer(),
            })
        }
    }

    const liveLocationSocket = useLiveLocationStore(
        useShallow((s) => s.liveLocationSocket)
    )

    if (
        disableLocationServices === true ||
        !ALLOWED_TIERS.includes(tier ?? '') ||
        isGuestMode ||
        liveLocationSocket.disconnected
    ) {
        return null
    }

    return bottomFab ? (
        <Fab
            style={{
                backgroundColor: ColoursObj.shades.shade4,
            }}
            size="medium"
            onClick={onClick}
        >
            <Icon
                name="Share_Location"
                width="28px"
                height="28px"
                iconColour={
                    sharingIsActive ? ColoursObj.states.success : undefined
                }
            />
        </Fab>
    ) : (
        <Icon
            name="Share_Location"
            iconColour={
                sharingIsActive
                    ? ColoursObj.states.success
                    : ColoursObj.shades.shade5
            }
            onClick={onClick}
        />
    )
}
